import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';

import {
  compatibilityCheck,
  getCompatInvite,
  acceptCompatInvite,
  type CompatBasic,
} from '@/api/client';
import OrnateFrame from '@/components/OrnateFrame';
import RichText from '@/components/RichText';
import ShareButton from '@/components/ShareButton';
import DateField from '@/components/DateField';
import { usePayment } from '@/hooks/usePayment';
import { useSubscription } from '@/hooks/useSubscription';

/**
 * Compatibility screen — two-card pull-apart animation, basic score free,
 * full breakdown paywalled.
 *
 * Two entry modes:
 *   1. Direct (Маша in her own app) → /compat
 *      Form: friend's name + DOB. Result + paywall.
 *   2. From deep-link Паша clicked → /compat/<UUID>
 *      We pre-load Маша's teaser, ask only Паша's DOB, show basic + paywall.
 */
export default function Compat() {
  const { inviteId } = useParams<{ inviteId?: string }>();
  const { t } = useTranslation();

  const [teaser, setTeaser] = useState<{
    inviter_display_name: string;
    inviter_sign: string;
  } | null>(null);
  const [teaserError, setTeaserError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [birth, setBirth] = useState('');
  const [basic, setBasic] = useState<CompatBasic | null>(null);
  const [full, setFull] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { pay, paying, payError } = usePayment('compatibility_full');
  const { isPriestess } = useSubscription();
  const [unlockingFree, setUnlockingFree] = useState(false);

  const requestFullBreakdown = async () => {
    try {
      const r = await compatibilityCheck({
        other_name: name || 'Friend',
        other_birth_date: birth,
        full: true,
      });
      if (r.full) setFull(r.full);
    } catch {}
  };

  const onUnlockFull = () => {
    if (isPriestess) {
      setUnlockingFree(true);
      requestFullBreakdown().finally(() => setUnlockingFree(false));
      return;
    }
    pay(requestFullBreakdown);
  };

  // Load invite teaser if we arrived via deep-link.
  useEffect(() => {
    if (!inviteId) return;
    getCompatInvite(inviteId)
      .then(setTeaser)
      .catch((e) => setTeaserError((e as Error).message));
  }, [inviteId]);

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      let data: { basic: CompatBasic };
      if (inviteId) {
        data = await acceptCompatInvite(inviteId, {
          invitee_name: name || 'Friend',
          invitee_birth: birth,
        });
      } else {
        const r = await compatibilityCheck({
          other_name: name,
          other_birth_date: birth,
          full: false,
        });
        data = { basic: r.basic };
      }
      setBasic(data.basic);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const inInviteMode = !!inviteId;

  return (
    <main className="mx-auto max-w-md px-5 pb-24 pt-2">
      <h1 className="mb-2 text-center font-serif text-2xl text-gold-300">
        {inInviteMode && teaser
          ? t('compatibility.subtitle_from_link', { name: teaser.inviter_display_name })
          : t('compatibility.title')}
      </h1>
      {teaserError && (
        <p className="mb-4 text-center text-xs text-rose-400">
          {teaserError}
        </p>
      )}

      <AnimatePresence mode="wait">
        {!basic ? (
          <motion.form
            key="form"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="space-y-4"
            onSubmit={(e) => { e.preventDefault(); submit(); }}
          >
            {!inInviteMode && (
              <Field label={t('compatibility.other_name')}>
                <input
                  className="mystic-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={64}
                  autoFocus
                />
              </Field>
            )}
            <Field
              label={
                inInviteMode
                  ? t('compatibility.your_birth_only')
                  : t('compatibility.other_birth')
              }
            >
              <DateField
                value={birth}
                onChange={setBirth}
                autoFocus={inInviteMode}
              />
            </Field>

            <button
              type="submit"
              className="btn-primary w-full disabled:opacity-40"
              disabled={!birth || submitting}
            >
              {submitting
                ? t('compatibility.calculating')
                : t('compatibility.calculate')}
            </button>

            {error && (
              <p className="text-center text-xs text-rose-400">{error}</p>
            )}
          </motion.form>
        ) : (
          <motion.section
            key="result"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center"
          >
            {/* Cards in a flex row — no absolute positioning, no overlap.
                The pull-apart feel is preserved by animating each card from
                a small centred state outward. */}
            <div className="mb-3 flex items-center justify-center gap-4">
              <motion.div
                initial={{ x: 40, opacity: 0, scale: 0.85 }}
                animate={{ x: 0, opacity: 1, scale: 1 }}
                transition={{ delay: 0.1, duration: 0.6, ease: 'easeOut' }}
              >
                <MiniCard sign={basic.me_sign} variant="me" />
              </motion.div>
              <motion.div
                initial={{ x: -40, opacity: 0, scale: 0.85 }}
                animate={{ x: 0, opacity: 1, scale: 1 }}
                transition={{ delay: 0.1, duration: 0.6, ease: 'easeOut' }}
              >
                <MiniCard sign={basic.other_sign} variant="other" />
              </motion.div>
            </div>

            {/* Score in its own row underneath — clean, no overlap possible. */}
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.5 }}
              className="mb-6 flex flex-col items-center"
            >
              <div className="flex h-24 w-24 flex-col items-center justify-center
                              rounded-full border-2 border-gold-400
                              bg-night-900 shadow-[0_0_30px_rgba(232,195,86,0.4)]">
                <div className="font-serif text-3xl text-gold-300">
                  {basic.average}%
                </div>
              </div>
              <div className="mt-2 text-[10px] uppercase tracking-widest text-gold-300/70">
                {t('compatibility.baseline_match')}
              </div>
            </motion.div>

            <OrnateFrame className="card-glow mb-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <ScoreBar
                  label={t('compatibility.mental')}
                  value={basic.mental}
                />
                <ScoreBar
                  label={t('compatibility.emotional')}
                  value={basic.emotional}
                />
              </div>
              <p className="mt-4 text-left font-serif italic leading-relaxed text-gold-200">
                {basic.teaser}
              </p>
            </OrnateFrame>

            {full && (
              <div className="mb-4 rounded-2xl border border-gold-500/30 bg-night-800/40 p-4 text-left">
                <RichText
                  text={full}
                  className="font-serif italic leading-relaxed text-sm text-gold-200"
                />
              </div>
            )}

            {/* Share — viral hook. Only meaningful for Маша (the inviter),
                not for Паша who arrived through someone's link. */}
            {!inInviteMode && (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9, duration: 0.5 }}
                className="mb-4"
              >
                <OrnateFrame className="card-glow border-gold-500/50">
                  <div className="mb-2 text-center text-[10px] uppercase
                                  tracking-[0.25em] text-gold-500/70">
                    ✦ ✦ ✦
                  </div>
                  <h3 className="mb-1 text-center font-serif text-lg text-gold-200">
                    {t('compatibility.share_invite_title')}
                  </h3>
                  <p className="mb-4 text-center text-xs italic leading-relaxed text-gold-300/70">
                    {t('compatibility.share_invite_blurb')}
                  </p>
                  <ShareButton shareMessage={t('compatibility.share_message')} />
                </OrnateFrame>
              </motion.div>
            )}

            {!full && (
              <>
                <button
                  type="button"
                  /* Маша sees share as the primary CTA; the paywall is
                     secondary (ghost). Паша has no one to share to yet,
                     so the paywall stays primary for him. */
                  className={`${inInviteMode ? 'btn-primary' : 'btn-ghost'}
                              w-full disabled:opacity-40`}
                  disabled={paying || unlockingFree}
                  onClick={onUnlockFull}
                >
                  {paying || unlockingFree
                    ? t('payment.opening')
                    : isPriestess
                      ? t('compatibility.unlock_priestess')
                      : t('compatibility.unlock_full', { stars: 30 })}
                </button>
                {isPriestess && (
                  <p className="mt-2 text-center text-[10px] uppercase tracking-widest text-gold-400/70">
                    ✦ {t('compatibility.included_priestess')}
                  </p>
                )}
              </>
            )}

            {payError && (
              <p className="mt-2 text-center text-xs text-rose-400">{payError}</p>
            )}

            <button
              type="button"
              className="mt-3 text-xs text-gold-300/60 underline-offset-4 hover:underline"
              onClick={() => { setBasic(null); setFull(null); setName(''); setBirth(''); }}
            >
              {t('compatibility.new_check')}
            </button>
          </motion.section>
        )}
      </AnimatePresence>

      <style>{`
        .mystic-input {
          width: 100%;
          background: rgba(11, 8, 32, 0.6);
          border: 1px solid rgba(212, 168, 46, 0.3);
          color: #fff6c7;
          font-family: 'Cormorant Garamond', Georgia, serif;
          font-size: 1.15rem;
          padding: 0.85rem 1rem;
          border-radius: 0.75rem;
          outline: none;
          transition: border-color 0.2s;
        }
        .mystic-input:focus { border-color: #e8c356; }
      `}</style>
    </main>
  );
}


function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm text-gold-300/80">{label}</span>
      {children}
    </label>
  );
}


function ScoreBar({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between">
        <span className="text-xs text-gold-300/70">{label}</span>
        <span className="font-serif text-base text-gold-300">{value}%</span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-night-900">
        <motion.div
          className="h-full bg-gradient-to-r from-gold-500 to-gold-300"
          initial={{ width: 0 }}
          animate={{ width: `${value}%` }}
          transition={{ duration: 1, ease: 'easeOut' }}
        />
      </div>
    </div>
  );
}


/**
 * Tiny stylised "compatibility card" — shows a zodiac symbol on a small
 * ornate rectangle. Two of these slide apart to reveal the score.
 */
function MiniCard({ sign, variant }: { sign: string; variant: 'me' | 'other' }) {
  const symbol: Record<string, string> = {
    aries: '♈', taurus: '♉', gemini: '♊', cancer: '♋',
    leo: '♌', virgo: '♍', libra: '♎', scorpio: '♏',
    sagittarius: '♐', capricorn: '♑', aquarius: '♒', pisces: '♓',
  };
  const tint =
    variant === 'me' ? 'from-gold-500/40 to-night-700' : 'from-rose-400/40 to-night-700';
  return (
    <div
      className={`flex h-40 w-28 flex-col items-center justify-center rounded-xl
                  border border-gold-500/40 bg-gradient-to-br ${tint}`}
    >
      <div className="text-4xl text-gold-200">{symbol[sign] ?? '✦'}</div>
      <div className="mt-2 text-[10px] uppercase tracking-widest text-gold-300/80">
        {sign}
      </div>
    </div>
  );
}
